import { Clear, Plus } from "@procore/core-icons";
import { Box, Button, Input, Search, spacing, ToggleButton } from "@procore/core-react";
import { useState } from "react";

import { useAppDispatch } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CameraType, renderActions } from "features/render";
import { getSelectionBoundingSphere } from "utils/objectData";

import { Section, StatusTooltip } from "./common";
import { parseIdList } from "./parseIdList";
import { useActionStatus } from "./useActionStatus";

const EXTERNAL_ID_LENGTH = 32;

type PropertyRow = { property: string; value: string };

/**
 * Selects matching objects, replacing the selection, and zooms to them. Finds by ids (32 characters
 * long for external ids, internal otherwise), or by property values ANDed together; an empty value matches any.
 */
export function FindSection() {
    const {
        state: { webviewer, dataAccess },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const [keyValue, setKeyValue] = useState(false);
    const [query, setQuery] = useState("");
    const [rows, setRows] = useState<PropertyRow[]>([{ property: "", value: "" }]);
    const [foundCount, setFoundCount] = useState(0);
    const [status, run, error] = useActionStatus();
    const tokens = parseIdList(query);
    const terms = rows
        .filter((row) => row.property.trim())
        .map(({ property, value }) =>
            value.trim()
                ? { property: property.trim(), anyOf: [value.trim()] }
                : { property: property.trim(), defined: true as const },
        );
    const disabled = !webviewer || !dataAccess || (keyValue ? terms.length === 0 : tokens.length === 0);

    const findIds = async () => {
        if (keyValue) {
            return Array.from(await dataAccess!.findIdsByProperties({ terms }));
        }
        const isExternal = (token: string) => token.length === EXTERNAL_ID_LENGTH;
        return [
            ...tokens.filter((token) => !isExternal(token)).map(Number),
            ...(await webviewer!.publicIdsToObjectIds(tokens.filter(isExternal))),
        ].filter((id) => Number.isInteger(id));
    };

    const find = () =>
        run(async () => {
            const ids = await findIds();
            setFoundCount(ids.length);
            if (ids.length === 0) {
                return;
            }
            await webviewer!.model.deselectAllObjects();
            await webviewer!.model.selectObjectsByInternalIds(ids);

            const zoomTo = await getSelectionBoundingSphere({ ids, dataAccess: dataAccess! });
            if (zoomTo) {
                dispatch(renderActions.setCamera({ type: CameraType.Pinhole, zoomTo }));
            }
        }, "Failed to find objects");

    const foundText = foundCount === 0 ? "No objects found" : `Selected ${foundCount}`;
    const submit = () => !disabled && void find();
    const updateRow = (idx: number, patch: Partial<PropertyRow>) =>
        setRows((rows) => rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

    return (
        <Section
            title="Find"
            action={
                <ToggleButton size="sm" selected={keyValue} onClick={() => setKeyValue(!keyValue)}>
                    Key-value
                </ToggleButton>
            }
        >
            {keyValue ? (
                <form
                    style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        submit();
                    }}
                >
                    {rows.map((row, idx) => (
                        <Box key={idx} style={{ display: "flex", gap: spacing.xs, alignItems: "center" }}>
                            <Input
                                value={row.property}
                                onChange={(e) => updateRow(idx, { property: e.target.value })}
                                placeholder="Property"
                                aria-label="Property"
                                autoComplete="off"
                            />
                            <Input
                                value={row.value}
                                onChange={(e) => updateRow(idx, { value: e.target.value })}
                                placeholder="Value (empty: any)"
                                aria-label="Value"
                                autoComplete="off"
                            />
                            <Button
                                icon={<Clear />}
                                variant="tertiary"
                                size="sm"
                                aria-label="Remove property"
                                disabled={rows.length === 1}
                                onClick={() => setRows((rows) => rows.filter((_, i) => i !== idx))}
                            />
                        </Box>
                    ))}
                    <Box style={{ display: "flex", justifyContent: "space-between" }}>
                        <Button
                            icon={<Plus />}
                            variant="tertiary"
                            size="sm"
                            onClick={() => setRows((rows) => [...rows, { property: "", value: "" }])}
                        >
                            Add
                        </Button>
                        <StatusTooltip status={status} doneText={foundText} error={error}>
                            <Button
                                type="submit"
                                variant="secondary"
                                size="sm"
                                disabled={disabled}
                                loading={status === "running"}
                            >
                                Find
                            </Button>
                        </StatusTooltip>
                    </Box>
                </form>
            ) : (
                <StatusTooltip status={status} doneText={foundText} error={error}>
                    <Search
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onClear={() => setQuery("")}
                        onSubmit={submit}
                        placeholder="IDs, comma separated or JSON array"
                        autoComplete="off"
                    />
                </StatusTooltip>
            )}
        </Section>
    );
}
